import { useState } from 'react';
import { AlertTriangle, FilePlus2, FileText, Loader2, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { resumeMarketingService } from '../services/resumeMarketing.service';

/**
 * Opens Resume Builder via HRMS SSO (no separate login).
 * Visible only for Resume Marketing Employee / TL sidebars.
 */
export function ResumeBuilderSsoButton({
  className = '',
  returnPath,
  targetPath = '/dashboard/resumes/new',
  nested = false,
  label = 'Create resume',
  icon: Icon = FilePlus2,
}) {
  const [loading, setLoading] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  const openResumeBuilder = async () => {
    setLoading(true);
    try {
      const result = await resumeMarketingService.createResumeBuilderSso({
        returnPath: returnPath || `${window.location.pathname}${window.location.search}`,
        targetPath,
      });
      const url = result?.data?.url;
      if (!url) {
        throw new Error('Resume Builder URL was not returned.');
      }
      setShowWarning(false);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Unable to open Resume Builder.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    if (loading) return;
    setShowWarning(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={loading}
        className={
          className ||
          'mx-2 my-0.5 flex w-[calc(100%-1rem)] items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-content-secondary transition-colors hover:bg-gray-100 disabled:opacity-60'
        }
      >
        {loading ? (
          <Loader2 className={`${nested ? 'h-4 w-4' : 'h-5 w-5'} shrink-0 animate-spin text-content-muted`} />
        ) : (
          <Icon className={`${nested ? 'h-4 w-4' : 'h-5 w-5'} shrink-0 text-content-muted`} />
        )}
        <span className="truncate">{loading ? 'Opening…' : label}</span>
      </button>

      {showWarning && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="resume-builder-sso-title"
        >
          <div className="w-full max-w-md rounded-2xl border border-default bg-surface p-6 shadow-xl">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
                  <AlertTriangle className="h-5 w-5" />
                </span>
                <div>
                  <h3
                    id="resume-builder-sso-title"
                    className="text-lg font-semibold text-content-primary"
                  >
                    Open Resume Builder?
                  </h3>
                  <p className="mt-2 text-sm text-content-secondary">
                    You are leaving People Desk. Resume Builder will open in a new tab and sign
                    you in automatically. Use <strong>Go back</strong> inside the builder when you
                    want to return here.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowWarning(false)}
                disabled={loading}
                className="rounded-lg p-1 text-content-muted hover:bg-gray-100 hover:text-content-primary disabled:opacity-50"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowWarning(false)}
                disabled={loading}
                className="rounded-xl border border-default px-4 py-2 text-sm font-medium text-content-secondary hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void openResumeBuilder()}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Opening…
                  </>
                ) : (
                  'Continue'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
